import { useEffect, useState, type ReactNode } from "react";
import { Bell as BellIcon, ChevronDown, ChevronRight, ChevronsUpDown, ExternalLink, Eye, Menu, PanelLeftClose, PanelLeftOpen } from "lucide-react";
import { Link, Outlet, useNavigate, useRouterState } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, storePath } from "../lib/api";
import { logout } from "../lib/auth";
import { languages, useI18n } from "../lib/i18n";
import { meQuery, useStore } from "../lib/store";
import { useStoreEvents } from "../lib/realtime";
import { flatLinks, matchPath, modules, settingsGroups, type NavLink } from "../lib/navigation";
import { Banner, Button, cx, ErrorState, Skeleton, useToast } from "./ui";
import { PlatformBanners, SupportStrip } from "./PlatformBanners";
import { CommandPalette } from "./CommandPalette";
import { NAV_STROKE, navIcons } from "./nav-icons";

// Per-viewer UI memory (collapsed rail, closed module groups).
function useStored<T>(key: string, initial: T): [T, (v: T) => void] {
  const [v, setV] = useState<T>(() => {
    try {
      const raw = localStorage.getItem(key);
      return raw === null ? initial : (JSON.parse(raw) as T);
    } catch {
      return initial;
    }
  });
  const set = (x: T) => {
    setV(x);
    try { localStorage.setItem(key, JSON.stringify(x)); } catch { /* storage unavailable */ }
  };
  return [v, set];
}

// LanguageSwitch: a toggle with two languages, a menu with more.
export function LanguageSwitch({ className }: { className?: string }) {
  const { locale, setLocale } = useI18n();
  const others = languages.filter((l) => l.code !== locale);
  if (others.length === 0) return null;
  if (others.length === 1) {
    return (
      <button lang={others[0].code} className={cx("h-9 rounded-md px-2.5 text-sm text-ink-muted hover:bg-muted hover:text-ink", className)}
        onClick={() => setLocale(others[0].code)}>
        {others[0].endonym}
      </button>
    );
  }
  return (
    <select aria-label="Language" value={locale} onChange={(e) => setLocale(e.target.value)}
      className={cx("h-9 rounded-md bg-transparent px-2 text-sm text-ink-muted hover:bg-muted hover:text-ink", className)}>
      {languages.map((l) => <option key={l.code} value={l.code} lang={l.code}>{l.endonym}</option>)}
    </select>
  );
}

function Avatar({ size = "md" }: { size?: "md" | "sm" }) {
  const me = useQuery(meQuery);
  const initials = (me.data?.name || me.data?.email || "?").slice(0, 1).toUpperCase();
  return (
    <span className={cx("flex shrink-0 items-center justify-center rounded-full bg-brand-tint font-semibold text-brand", size === "sm" ? "size-7 text-xs" : "size-8 text-xs")}>{initials}</span>
  );
}

// UserMenu: account links and sign-out. "inline" is the sidebar footer row.
function UserMenu({ inline, collapsed }: { inline?: boolean; collapsed?: boolean }) {
  const { t } = useI18n();
  const me = useQuery(meQuery);
  const [open, setOpen] = useState(false);
  const item = "block rounded-md px-3 py-2 text-ink-muted hover:bg-muted hover:text-ink";
  return (
    <div className="relative">
      {inline ? (
        <button aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen(!open)} title={collapsed ? me.data?.email : undefined}
          className={cx("flex w-full items-center gap-2.5 rounded-lg px-2 py-1.5 text-start hover:bg-muted", collapsed && "justify-center")}>
          <Avatar size="sm" />
          {!collapsed && <span className="min-w-0 flex-1"><span className="block truncate text-sm font-medium text-ink">{me.data?.name || me.data?.email}</span>
            <span className="block truncate text-xs text-ink-muted">{me.data?.email}</span></span>}
        </button>
      ) : (
        <button aria-haspopup="menu" aria-expanded={open} aria-label={me.data?.email} onClick={() => setOpen(!open)} className="rounded-full"><Avatar /></button>
      )}
      {open && (
        <div role="menu" className={cx("anim-pop absolute z-40 w-60 rounded-xl border border-line bg-white p-1 text-sm text-ink shadow-pop",
          inline ? "bottom-full start-0 mb-2" : "end-0 mt-2")} onMouseLeave={() => setOpen(false)}>
          <div className="px-3 py-2">
            <div className="font-medium">{me.data?.name}</div>
            <div className="truncate text-ink-muted">{me.data?.email}</div>
          </div>
          <Link to="/stores" className={item}>{t("user.stores")}</Link>
          <a href={`${import.meta.env.VITE_ACCOUNTS_URL}/account`} className={item}>{t("user.account")}</a>
          <button onClick={logout} className={cx(item, "w-full text-start")}>{t("user.logout")}</button>
        </div>
      )}
    </div>
  );
}

type Notification = { id: string; type: string; title: string; body: string; link: string; severity: string; read: boolean; created_at: string };

function Bell({ storeId }: { storeId: string }) {
  const { t, relative, locale } = useI18n();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const key = ["store", storeId, "/notifications", locale];
  const list = useQuery({ queryKey: key, queryFn: () => api<{ data: Notification[]; unread_count: number }>(storePath(storeId, "/notifications")) });
  const readAll = useMutation({
    mutationFn: () => api(storePath(storeId, "/notifications/read-all"), { method: "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: key }),
  });
  const openItem = async (n: Notification) => {
    setOpen(false);
    if (!n.read) api(storePath(storeId, `/notifications/${n.id}/read`), { method: "POST" }).then(() => qc.invalidateQueries({ queryKey: key }));
    try {
      const u = new URL(n.link);
      if (u.origin === location.origin) navigate({ to: u.pathname });
      else location.assign(n.link);
    } catch {
      /* no link */
    }
  };
  const unread = list.data?.unread_count ?? 0;
  return (
    <div className="relative">
      <button aria-expanded={open} onClick={() => setOpen(!open)} className="relative flex size-9 items-center justify-center rounded-md text-ink-muted hover:bg-muted hover:text-ink">
        <BellIcon className="size-[18px]" strokeWidth={NAV_STROKE} aria-hidden />
        <span className="sr-only">{t("bell.title")}</span>
        {unread > 0 && <span className="absolute end-1 top-1 min-w-4 rounded-full bg-brand px-1 text-[10px] font-semibold leading-4 text-white">{unread}</span>}
      </button>
      {open && (
        <div className="anim-pop absolute end-0 z-40 mt-2 w-80 overflow-hidden rounded-xl border border-line bg-white text-ink shadow-pop">
          <header className="flex items-center justify-between border-b border-line px-4 py-2.5">
            <span className="text-sm font-semibold">{t("bell.title")}</span>
            {unread > 0 && <Button size="sm" variant="plain" onClick={() => readAll.mutate()}>{t("bell.mark_all")}</Button>}
          </header>
          <ul className="max-h-96 overflow-y-auto">
            {list.data?.data.length === 0 && <li className="p-4 text-sm text-ink-muted">{t("bell.empty")}</li>}
            {list.data?.data.map((n) => (
              <li key={n.id}>
                <button onClick={() => openItem(n)} className={cx("block w-full px-4 py-2.5 text-start text-sm hover:bg-subtle", !n.read && "bg-brand-tint/60")}>
                  <div className={cx(!n.read && "font-semibold")}>{n.title}</div>
                  {n.body && <div className="text-ink-muted">{n.body}</div>}
                  <div className="text-xs text-ink-subtle">{relative(n.created_at)}</div>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// The Sooquna bag mark (brand/build.py): where the store's name sits beside it.
function Mark() {
  return <img src="/mark.png" alt="" aria-hidden width={28} height={28} className="size-7 shrink-0" />;
}

// The Sooquna logo (brand/build.py): the lockup from small tablets up, the
// bag mark alone on phones.
function Logo() {
  return (
    <>
      <img src="/mark.png" alt="Sooquna" width={28} height={28} className="size-7 shrink-0 sm:hidden" />
      <img src="/logo-horizontal.png" alt="Sooquna" height={36} className="hidden h-9 w-auto sm:block" />
    </>
  );
}

// TopBar: pages outside a store (store chooser, onboarding, invitations).
export function TopBar({ children }: { children?: ReactNode }) {
  return (
    <header className="sticky top-0 z-20 flex h-14 items-center gap-2 border-b border-line bg-white px-4 sm:px-6">
      <Link to="/stores" className="flex items-center gap-2 text-[15px] font-semibold tracking-tight text-ink">
        <Logo />
      </Link>
      <div className="flex flex-1 items-center gap-1">{children}</div>
      <LanguageSwitch />
      <UserMenu />
    </header>
  );
}

function TrialBanner() {
  const { t } = useI18n();
  const { summary, handle } = useStore();
  const s = summary.data;
  if (!s) return null;
  if (s.is_development) return <div className="px-4 pt-4 sm:px-8"><Banner tone="info">{t("dev.banner")}</Banner></div>;
  const sub = s.subscription;
  // An unpaid bill: a frozen store's admin is billing-only.
  if (sub.status === "past_due" || sub.status === "frozen")
    return (
      <div className="px-4 pt-4 sm:px-8">
        <Banner tone={sub.status === "frozen" ? "critical" : "warning"} action={<Link to="/store/$handle/settings/subscription" params={{ handle }}><Button>{t("bill.pay_now")}</Button></Link>}>
          {t(sub.status === "frozen" ? "bill.banner_frozen" : "bill.banner_past_due")}</Banner>
      </div>
    );
  if (sub.status !== "trialing" || sub.trial_days_left === undefined || sub.trial_days_left > 7) return null;
  const days = sub.trial_days_left;
  const text = days <= 0 ? (sub.trial_ends_at && new Date(sub.trial_ends_at) < new Date() ? t("trial.banner_ended") : t("trial.banner_today")) : t("trial.banner", { days });
  return (
    <div className="px-4 pt-4 sm:px-8">
      <Banner tone={days <= 1 ? "critical" : "warning"} action={<Link to="/store/$handle/settings/subscription" params={{ handle }}><Button>{t("trial.choose_plan")}</Button></Link>}>{text}</Banner>
    </div>
  );
}

// Breadcrumbs: module › page (› settings section), from the nav structure.
function Breadcrumbs({ rel }: { rel: string }) {
  const { t } = useI18n();
  const { handle, can } = useStore();
  const crumbs: { label: string; to?: string }[] = [];
  if (rel === "/settings" || rel.startsWith("/settings/")) {
    crumbs.push({ label: t("nav.settings"), to: "/settings" });
    for (const g of settingsGroups) {
      const sec = g.sections.find((s) => matchPath(rel, "/settings" + s.to));
      if (sec) {
        crumbs.push({ label: t(g.label) }, { label: t(sec.label), to: "/settings" + sec.to });
      }
    }
  } else {
    // Most specific link; on a tie (Catalog and its Products child share
    // /products) the child, so the trail shows its parent.
    const hit = flatLinks(can).filter((x) => matchPath(rel, x.link.to))
      .sort((a, b) => b.link.to.length - a.link.to.length || Number(!!b.parent) - Number(!!a.parent))[0];
    if (hit) {
      if (hit.module.label) crumbs.push({ label: t(hit.module.label) });
      if (hit.parent && hit.parent.to !== hit.link.to) crumbs.push({ label: t(hit.parent.label), to: hit.parent.to });
      crumbs.push({ label: t(hit.link.label), to: hit.link.to });
    }
  }
  if (crumbs.length === 0) return null;
  return (
    <nav aria-label={t("nav.breadcrumb")} className="hidden min-w-0 items-center gap-1.5 text-sm md:flex">
      {crumbs.map((c, i) => {
        const last = i === crumbs.length - 1;
        return (
          <span key={i} className="flex min-w-0 items-center gap-1.5">
            {i > 0 && <ChevronRight className="size-3.5 shrink-0 text-ink-subtle rtl:-scale-x-100" aria-hidden />}
            {c.to !== undefined && !last
              ? <Link to={`/store/$handle${c.to}`} params={{ handle }} className="truncate text-ink-muted hover:text-ink">{c.label}</Link>
              : <span className={cx("truncate", last ? "font-medium text-ink" : "text-ink-muted")} aria-current={last ? "page" : undefined}>{c.label}</span>}
          </span>
        );
      })}
    </nav>
  );
}

export function StoreLayout() {
  const { t } = useI18n();
  const { handle, id, summary, lookup, can } = useStore();
  const qc = useQueryClient();
  const toast = useToast();
  const [menu, setMenu] = useState(false);
  const [collapsed, setCollapsed] = useStored("sooquna.nav.collapsed", false);
  const [closed, setClosed] = useStored<string[]>("sooquna.nav.closed", []);
  const pathname = useRouterState({ select: (st) => st.location.pathname });

  useEffect(() => {
    if (id) api(`/admin/v1/me/stores/${id}/visit`, { method: "POST" }).catch(() => {});
  }, [id]);

  useStoreEvents(id, (e) => {
    if (e.type === "notification.created") {
      qc.invalidateQueries({ queryKey: ["store", id, "/notifications"] });
      const d = e.data as { title?: string } | null;
      if (d?.title) toast(d.title);
    }
    if (e.type.startsWith("asset.")) {
      qc.invalidateQueries({ queryKey: ["store", id, "files"] });
      qc.invalidateQueries({ queryKey: ["store", id, "product"] });
    }
    if (e.type.startsWith("job.")) qc.invalidateQueries({ queryKey: ["store", id, "jobs", (e.data as { id?: string } | null)?.id] });
    if (e.type.startsWith("provisioning.")) qc.invalidateQueries({ queryKey: ["store", id] });
    if (e.type === "domain.updated") qc.invalidateQueries({ queryKey: ["store", id, "domains"] });
  });

  if (lookup.isError) return (<><TopBar /><div className="p-6"><ErrorState error={lookup.error} /></div></>);
  const s = summary.data;
  const rel = pathname.slice(`/store/${handle}`.length).replace(/\/$/, "");
  // The most specific link wins: /customers/segments is Segments, not also Customers.
  const links = flatLinks(can);
  const activeTo = links.map((x) => x.link.to).filter((to) => matchPath(rel, to)).sort((a, b) => b.length - a.length)[0];
  const inSettings = rel === "/settings" || rel.startsWith("/settings/");

  // Opens the live storefront through a staff preview link. The tab opens
  // first (popup blockers allow it), then goes to the link.
  const viewStore = async () => {
    const tab = window.open("about:blank", "_blank");
    try {
      const p = await api<{ url: string }>(storePath(id!, "/themes/live/preview"), { body: {} });
      if (tab) tab.location.href = p.url; else window.open(p.url, "_blank");
    } catch {
      tab?.close();
      if (summary.data?.storefront_url) window.open(summary.data.storefront_url, "_blank", "noopener");
    }
  };

  const rail = collapsed && !menu;
  const itemCls = (active: boolean, soon?: boolean) => cx(
    "relative flex h-9 items-center gap-2.5 rounded-lg text-sm",
    rail ? "justify-center px-0" : "px-2.5",
    active ? "bg-brand-tint font-medium text-brand before:absolute before:inset-y-1.5 before:start-0 before:w-0.5 before:rounded-full before:bg-brand"
      : "text-ink-muted hover:bg-muted hover:text-ink",
    soon && !active && "text-ink-subtle",
  );

  const navLink = (l: NavLink, sub = false) => {
    const Icon = navIcons[l.icon];
    // A parent whose first child is the same page (Catalog › Products)
    // reads as open, not selected: the child carries the selection.
    const sharesChild = !sub && (l.children ?? []).some((c) => c.to === l.to);
    const active = activeTo === l.to && !sharesChild;
    const openParent = !sub && !active && (l.children ?? []).some((c) => activeTo === c.to);
    const eye = l.to === "/storefront/themes";
    const link = (
      <Link key={l.to + l.label} to={`/store/$handle${l.to}`} params={{ handle }} onClick={() => setMenu(false)}
        aria-current={active ? "page" : undefined} title={rail ? t(l.label) : undefined}
        className={cx(itemCls(active, l.soon), sub && !rail && "ms-[26px] h-8", eye && !rail && "pe-9", openParent && "font-medium text-ink")}>
        {(!sub || rail) && <Icon className="size-[18px] shrink-0" strokeWidth={NAV_STROKE} aria-hidden />}
        {!rail && <span className="flex-1 truncate">{t(l.label)}</span>}
        {!rail && l.soon && <span className="rounded-full border border-line px-1.5 text-[10px] leading-4 text-ink-subtle">{t("common.coming_soon")}</span>}
      </Link>
    );
    if (!eye || rail) return link;
    // The eye opens the storefront as staff (past the password).
    return (
      <div key={l.to} className="relative">
        {link}
        <button type="button" onClick={() => void viewStore()} title={t("nav.view_store")} aria-label={t("nav.view_store")}
          className="absolute inset-y-0 end-1 my-auto flex size-7 items-center justify-center rounded-md text-ink-muted hover:bg-brand-tint-strong hover:text-brand">
          <Eye className="size-4" strokeWidth={NAV_STROKE} />
        </button>
      </div>
    );
  };

  const sidebar = (
    <div className="flex h-full flex-col">
      <div className={cx("flex h-14 shrink-0 items-center gap-2 border-b border-line", rail ? "justify-center px-2" : "px-3")}>
        <Link to="/stores" className="flex min-w-0 flex-1 items-center gap-2 rounded-lg px-1.5 py-1 hover:bg-muted" title={t("user.stores")}>
          <Mark />
          {!rail && (<>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm font-semibold text-ink">{s?.name ?? lookup.data?.name ?? "…"}</span>
              <span className="block truncate text-xs text-ink-subtle">Sooquna</span>
            </span>
            <ChevronsUpDown className="size-4 shrink-0 text-ink-subtle" aria-hidden />
          </>)}
        </Link>
      </div>
      <nav className="flex-1 space-y-4 overflow-y-auto p-3" aria-label={t("nav.menu")}>
        {modules.map((m) => {
          const items = m.items.filter((it) => !it.perm || can(it.perm));
          if (items.length === 0) return null;
          const isClosed = !rail && m.label !== undefined && closed.includes(m.id);
          return (
            <div key={m.id} className="space-y-0.5">
              {m.label && !rail && (
                <button type="button" aria-expanded={!isClosed} onClick={() => setClosed(isClosed ? closed.filter((x) => x !== m.id) : [...closed, m.id])}
                  className="flex w-full items-center justify-between rounded-md px-2.5 pb-1 pt-1.5 text-[11px] font-semibold uppercase tracking-[0.06em] text-ink-subtle hover:text-ink-muted">
                  {t(m.label)}
                  <ChevronDown className={cx("size-3.5 transition-transform", isClosed && "-rotate-90 rtl:rotate-90")} aria-hidden />
                </button>
              )}
              {m.label && rail && <div className="mx-auto my-1 h-px w-6 bg-line" aria-hidden />}
              {!isClosed && items.map((it) => {
                const children = (it.children ?? []).filter((c) => (!c.perm || can(c.perm)) && c.to !== it.to);
                const open = !rail && (matchPath(rel, it.to) || children.some((c) => matchPath(rel, c.to)) || (it.children ?? []).some((c) => activeTo === c.to));
                return (
                  <div key={it.to + it.label}>
                    {navLink(it)}
                    {open && <div className="mt-0.5 space-y-0.5">{(it.children ?? []).filter((c) => !c.perm || can(c.perm)).map((c) => (c.to === it.to ? navLink({ ...c }, true) : navLink(c, true)))}</div>}
                  </div>
                );
              })}
            </div>
          );
        })}
      </nav>
      <div className="shrink-0 space-y-0.5 border-t border-line p-3">
        <Link to="/store/$handle/settings" params={{ handle }} onClick={() => setMenu(false)} title={rail ? t("nav.settings") : undefined}
          aria-current={inSettings ? "page" : undefined} className={itemCls(inSettings)}>
          <navIcons.settings className="size-[18px] shrink-0" strokeWidth={NAV_STROKE} aria-hidden />{!rail && <span>{t("nav.settings")}</span>}
        </Link>
        <a href="https://sooquna.com/help" target="_blank" rel="noreferrer" title={rail ? t("nav.help") : undefined} className={itemCls(false)}>
          <navIcons.help className="size-[18px] shrink-0" strokeWidth={NAV_STROKE} aria-hidden />{!rail && <span>{t("nav.help")}</span>}
        </a>
        <div className="pt-2"><UserMenu inline collapsed={rail} /></div>
        <button type="button" onClick={() => setCollapsed(!collapsed)} aria-label={t(collapsed ? "nav.expand" : "nav.collapse")}
          className={cx("mt-1 hidden h-8 w-full items-center gap-2 rounded-md px-2.5 text-xs text-ink-subtle hover:bg-muted hover:text-ink md:flex", rail && "justify-center px-0")}>
          {collapsed ? <PanelLeftOpen className="size-4 rtl:-scale-x-100" /> : <PanelLeftClose className="size-4 rtl:-scale-x-100" />}
          {!rail && <span>{t("nav.collapse")}</span>}
        </button>
      </div>
    </div>
  );

  return (
    <div className="flex min-h-full">
      <aside className={cx("sticky top-0 hidden h-dvh shrink-0 border-e border-line bg-subtle transition-[width] duration-200 ease-out md:block", collapsed ? "w-16" : "w-[248px]")}>{sidebar}</aside>
      {menu && (
        <div className="fixed inset-0 z-40 bg-ink/20 backdrop-blur-[2px] md:hidden" onClick={() => setMenu(false)}>
          <aside className="anim-sheet h-full w-[280px] border-e border-line bg-subtle" onClick={(e) => e.stopPropagation()}>{sidebar}</aside>
        </div>
      )}
      <div className="flex min-w-0 flex-1 flex-col">
        <SupportStrip />
        <header className="sticky top-0 z-30 flex h-14 items-center gap-2 border-b border-line bg-white px-3 sm:px-6">
          <button className="flex size-9 items-center justify-center rounded-md text-ink-muted hover:bg-muted md:hidden" aria-label={t("nav.menu")} onClick={() => setMenu(!menu)}><Menu className="size-[18px]" /></button>
          <Breadcrumbs rel={rel} />
          <div className="flex-1" />
          <CommandPalette />
          <LanguageSwitch className="hidden sm:block" />
          {s && <a href={s.storefront_url} target="_blank" rel="noreferrer" className="hidden h-9 items-center gap-1.5 rounded-md border border-line-strong px-3 text-sm text-ink hover:bg-subtle lg:flex">{t("nav.view_store")}<ExternalLink className="size-3.5 rtl:-scale-x-100" aria-hidden /></a>}
          {id && <Bell storeId={id} />}
          <UserMenu />
        </header>
        <main className="min-w-0 flex-1">
          <PlatformBanners />
          <TrialBanner />
          {summary.isPending ? <div className="p-6"><Skeleton lines={5} /></div> : summary.isError ? <div className="p-6"><ErrorState error={summary.error} retry={() => summary.refetch()} /></div> : <Outlet />}
        </main>
      </div>
    </div>
  );
}
